import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Sparkles } from "lucide-react";
import { api, resolveImg } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { euDate } from "@/lib/utils";
import { EditableText } from "@/components/site/edit/EditableText";

function daysAway(dateStr) {
  const d = new Date(dateStr + "T00:00:00");
  return Math.ceil((d - new Date()) / 86400000);
}

export function HolidayStrip() {
  const navigate = useNavigate();
  const { t, tl, lang } = useLang();
  const { data: holiday } = useQuery({
    queryKey: ["holiday", "upcoming"],
    queryFn: async () => (await api.get("/holidays/upcoming")).data,
  });

  if (!holiday) return null;
  const away = daysAway(holiday.date);
  const isNow = away <= 0;
  const range = holiday.end_date && holiday.end_date !== holiday.date ? `${euDate(holiday.date)} – ${euDate(holiday.end_date)}` : euDate(holiday.date);
  const awayLabel = away > 0 ? `${t("holiday.in_prefix")} ${away} ${away === 1 ? t("holiday.day") : t("holiday.days")}` : range;

  return (
    <section className="relative z-10 pt-8 sm:pt-10 pb-14 sm:pb-16" data-testid="holiday-strip-section">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <motion.button
          onClick={() => navigate("/holidays")}
          data-testid="holiday-strip-cta"
          initial={{ opacity: 0, y: 8 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.45 }}
          className="group flex items-center gap-3 sm:gap-4 w-full text-left rounded-2xl bg-[#0F172A] outline outline-1 outline-offset-[12px] outline-[#BE923C]/80 px-4 sm:px-6 py-3 sm:py-3.5 hover:bg-[#152238] hover:outline-[#BE923C] transition-[background-color,outline-color] duration-300"
        >
          <span className="shrink-0 w-9 h-9 rounded-xl overflow-hidden bg-[#BE923C]/20 flex items-center justify-center text-[#BE923C] group-hover:scale-105 transition-transform">
            {holiday.image_url
              ? <img src={resolveImg(holiday.image_url)} alt={tl(holiday, "name")} className="w-full h-full object-cover" />
              : <Sparkles size={18} />}
          </span>
          <span className="hidden sm:inline text-xs font-semibold uppercase tracking-[0.2em] text-[#BE923C]">{isNow ? t("holiday.now_eyebrow") : <EditableText k="holiday.eyebrow" as="span" />}</span>
          <span className="hidden sm:inline text-sm text-[#BE923C]/70">·</span>
          <span className="font-serif text-base sm:text-lg text-[#F9F8F6] leading-tight truncate">{tl(holiday, "name")}</span>
          <span className="hidden md:inline text-sm text-[#BE923C]/70">·</span>
          <span className="hidden md:inline text-sm text-white/65 truncate">{awayLabel}</span>
          <span className="ms-auto shrink-0 inline-flex items-center gap-1.5 rounded-full bg-[#BE923C] group-hover:bg-[#d0a54a] text-[#0F172A] text-sm font-semibold px-4 sm:px-6 py-2 transition-colors">
            <EditableText k="holiday.explore" as="span" /> <ArrowUpRight size={15} className="rtl:rotate-[-90deg]" />
          </span>
        </motion.button>
      </div>
    </section>
  );
}
